import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { setAuth } from './Redux/authSlice';
import { RootState } from './Redux/store';
import Charts from './Charts';
import { FaUserGraduate, FaChalkboardTeacher, FaMoneyBillWave, FaExclamationCircle, FaWallet, FaReceipt, FaUserTie, FaChartLine, FaArrowRight, FaClipboardList, FaBookOpen } from 'react-icons/fa';

const Dashboard = () => {
  const dispatch = useDispatch();
  const [userInfo, setUserInfo] = useState({ firstName: '', lastName: '', branchLogo: '', branchName: '', role: '' });

  const auth = useSelector((state: RootState) => state.auth.value);
  const [numStudents, setNumStudents] = useState(0);
  const [numTeachers, setNumTeachers] = useState(0);
  const [feesCount, setFeesCount] = useState(0);
  const [feesNotpaid, setNotpaid] = useState(0);
  const [currentDateTime] = useState(new Date());
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalSalary, setTotalSalary] = useState(0);
  const [netIncome, setNetIncome] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Teacher-specific stats
  const [teacherClasses, setTeacherClasses] = useState(0);
  const [teacherSections, setTeacherSections] = useState(0);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value).replace('KES', 'KSh');
  };

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.indexOf(cookieName) === 0) return cookie.substring(cookieName.length);
    }
    return '';
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = getCookie('token');
        if (!token) { dispatch(setAuth(false)); return; }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const userResponse = await axios.get('/users/user');
        const { first_name, last_name, branch_logo, branch_name, role } = userResponse.data.user;
        setUserInfo({ firstName: first_name, lastName: last_name, branchLogo: branch_logo, branchName: branch_name, role: role });

        // Fetch student count for all users
        const studentsRes = await axios.get('/Branches/students-count/');
        setNumStudents(studentsRes.data?.num_students || 0);

        // Fetch teacher-specific data if user is a teacher
        if (role === 'teacher') {
          try {
            const classesRes = await axios.get('/Branches/classes/');
            const sectionsRes = await axios.get('/Branches/sections/');
            setTeacherClasses(classesRes.data?.length || 0);
            setTeacherSections(sectionsRes.data?.length || 0);
          } catch (err) {
            console.error('Error fetching teacher stats:', err);
          }
        }

        // Only fetch financial and teacher data for admin/accountant/super_admin
        if (role !== 'teacher' && role !== 'parent') {
          const [feesRes, feesNotPaidRes, teachersRes, incomeRes] = await Promise.all([
            axios.get('/fees/fees-count/'),
            axios.get('/fees/fees_not_paid_count/'),
            axios.get('/Branches/teacher_count/'),
            axios.get('/fees/income/')
          ]);

          setFeesCount(feesRes.data?.num_fees_paid || 0);
          setNotpaid(feesNotPaidRes.data?.num_students_not_paid || 0);
          setNumTeachers(teachersRes.data?.num_teachers || 0);

          const currentMonth = currentDateTime.toLocaleString('default', { month: 'long' });
          if (incomeRes.data.income_and_expenses_by_month?.[currentMonth]) {
            const monthData = incomeRes.data.income_and_expenses_by_month[currentMonth];
            setTotalIncome(parseFloat(monthData.total_income?.replace(/,/g, '') || 0));
            setTotalExpenses(parseFloat(monthData.total_expenses?.replace(/,/g, '') || 0));
            setTotalSalary(parseFloat(monthData.total_salaries?.replace(/,/g, '') || 0));
            setNetIncome(parseFloat(monthData.net_income?.replace(/,/g, '') || 0));
          }
        }

        dispatch(setAuth(true));
      } catch (error) {
        console.error('Error fetching data:', error);
        dispatch(setAuth(false));
        setError('Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch, currentDateTime]);

  // Different card data based on user role
  const getCardData = () => {
    const isTeacher = userInfo.role === 'teacher';

    if (isTeacher) {
      // Teacher-specific dashboard cards
      return [
        { title: "Ardaydayda", value: numStudents, icon: FaUserGraduate, color: "text-primary", bg: "bg-primary", link: "/students" },
        { title: "Fasalladayda", value: teacherClasses, icon: FaBookOpen, color: "text-info", bg: "bg-info", link: "/classes" },
        { title: "Qeybahayga", value: teacherSections, icon: FaClipboardList, color: "text-success", bg: "bg-success", link: "/section" },
        { title: "Imtixaanada", value: 0, icon: FaChartLine, color: "text-warning", bg: "bg-warning", link: "/exams" }
      ];
    }

    // Admin/Accountant dashboard cards
    return [
      { title: "Wadarta Ardayda", value: numStudents, icon: FaUserGraduate, color: "text-primary", bg: "bg-primary", link: "/students" },
      { title: "Wadarta Macalimiinta", value: numTeachers, icon: FaChalkboardTeacher, color: "text-info", bg: "bg-info", link: "/teachers" },
      { title: "Lacagaha La Bixiyay", value: feesCount, icon: FaMoneyBillWave, color: "text-success", bg: "bg-success", link: "/fees" },
      { title: "Lacagaha Aan La Bixin", value: feesNotpaid, icon: FaExclamationCircle, color: "text-danger", bg: "bg-danger", link: "/fees_not_paid" },
      { title: "Wadarta Dakhliga", value: totalIncome, icon: FaWallet, color: "text-success", bg: "bg-success", link: "/income", isCurrency: true },
      { title: "Wadarta Kharashka", value: totalExpenses, icon: FaReceipt, color: "text-warning", bg: "bg-warning", link: "/ExpenseList", isCurrency: true },
      { title: "Mushahaarka", value: totalSalary, icon: FaUserTie, color: "text-primary", bg: "bg-primary", link: "/salaries", isCurrency: true },
      { title: "Dakhliga Saafiga ah", value: netIncome, icon: FaChartLine, color: netIncome >= 0 ? "text-success" : "text-danger", bg: netIncome >= 0 ? "bg-success" : "bg-danger", link: "/income", isCurrency: true }
    ];
  };

  const cardData = getCardData();
  const isTeacher = userInfo.role === 'teacher';

  if (!auth) return (
    <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
      <div className="text-center p-5 card-modern">
        <h4 className="text-danger mb-3">{error || "Ma Haysatid Ogolaansho"}</h4>
        <p className="mb-4 text-muted">Fadlan gasho si aad u hesho muuraayada</p>
        <Link to="/" className="btn btn-primary px-4 rounded-pill">Gasho</Link>
      </div>
    </div>
  );

  return (
    <div className="dashboard-content">
      {/* Dashboard Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-5">
        <div className="mb-3 mb-md-0">
          <h2 className="fw-bold mb-2 text-dark">
            {currentDateTime.getHours() < 12 ? 'Subax Wanaagsan' : currentDateTime.getHours() < 18 ? 'Galab Wanaagsan' : 'Habeen Wanaagsan'},
            <span className="text-primary ms-2">{userInfo.firstName || 'Walaal'}</span>
          </h2>
          <p className="text-muted mb-0 fs-5">
            {isTeacher ? "Warbixin guud ee fasalladaada iyo ardaydaada" : "Warbixin guud ee waxqabadka iskuulkaaga"}
          </p>
        </div>
        <div className="bg-white rounded-pill px-4 py-3 d-flex align-items-center shadow-sm border">
          <i className="far fa-calendar-alt me-2 text-primary"></i>
          <span className="text-dark fw-bold">
            {currentDateTime.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </span>
        </div>
      </div>

      {/* Dashboard Cards */}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '40vh' }}>
          <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Fadlan Sug...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="row g-4 mb-5">
            {cardData.map((card, index) => (
              <div key={index} className={`col-xl-${isTeacher ? '3' : '3'} col-lg-${isTeacher ? '6' : '4'} col-md-6`}>
                <Link to={card.link} className="text-decoration-none">
                  <div className="card-modern h-100 p-4 position-relative overflow-hidden group">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <p className="text-muted text-uppercase fw-bold mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                          {card.title}
                        </p>
                        <h3 className="fw-bold mb-0 text-dark">
                          {card.isCurrency ? (
                            <>
                              {card.value < 0 && <span className="text-danger">-</span>}
                              <span style={{ fontSize: '1.5rem' }}>{formatCurrency(Math.abs(card.value)).split('.')[0]}</span>
                              <small className="text-muted fs-6">.{formatCurrency(Math.abs(card.value)).split('.')[1]}</small>
                            </>
                          ) : (
                            card.value.toLocaleString()
                          )}
                        </h3>
                        {card.title === "Dakhliga Saafiga ah" && card.value < 0 && (
                          <small className="text-danger fw-bold d-block mt-1">
                            ⚠️ Kharashka ayaa ka badan dakhliga
                          </small>
                        )}
                      </div>
                      <div className={`rounded-3 p-3 d-flex align-items-center justify-content-center bg-opacity-10 ${card.bg}-subtle text-opacity-100`}
                        style={{ width: '48px', height: '48px', background: `var(--bs-${card.bg.replace('bg-', '')}-bg-subtle)` }}>
                        <card.icon className={`${card.color} fs-4`} />
                      </div>
                    </div>

                    <div className="d-flex align-items-center text-primary small fw-medium mt-3">
                      <span>Arag Faahfaahin</span>
                      <FaArrowRight className="ms-2" size={12} />
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          {/* Charts Section - Only show for non-teachers */}
          {!isTeacher && (
            <div className="row">
              <div className="col-12">
                <div className="card-modern p-4">
                  <div className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3">
                    <h5 className="fw-bold mb-0 text-dark">Falanqaynta Maaliyadda & Xaadirinta</h5>
                    <select className="form-select form-select-sm w-auto border-0 bg-light fw-medium">
                      <option>Sanadkan</option>
                      <option>Sanadkii Hore</option>
                    </select>
                  </div>
                  <Charts />
                </div>
              </div>
            </div>
          )}

          {/* Teacher Welcome Message */}
          {isTeacher && (
            <div className="row">
              <div className="col-12">
                <div className="card-modern p-5 text-center border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
                  <div className="mb-4">
                    <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
                      <FaUserGraduate size={48} className="text-primary" />
                    </div>
                  </div>
                  <h2 className="fw-bold mb-3 text-dark">Ku Soo Dhowow Muuraayada Macalinka!</h2>
                  <p className="fs-5 mb-4 text-muted mx-auto" style={{ maxWidth: '600px' }}>
                    Arag ardayda laguu xil saaray, qor xaadirinta, geli dhibcaha imtixaanka, oo maamul fasalladaada.
                  </p>
                  <div className="d-flex gap-3 justify-content-center flex-wrap">
                    <Link to="/students" className="btn btn-primary btn-lg px-5 rounded-pill shadow">
                      <FaUserGraduate className="me-2" />
                      Arag Ardayda
                    </Link>
                    <Link to="/attendance-table" className="btn btn-success btn-lg px-5 rounded-pill shadow">
                      <FaClipboardList className="me-2" />
                      Qor Xaadirinta
                    </Link>
                    <Link to="/add-marks" className="btn btn-warning btn-lg px-5 rounded-pill shadow text-white">
                      <FaChartLine className="me-2" />
                      Geli Dhibcaha
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;